<template>
  <RichTextRenderer
    :document="text"
    :node-renderers="nodesRenderer"
    :mark-renderers="markRenderers"
  />
</template>

<script setup lang="ts">
import RichTextRenderer, {
  type MarksRenderer,
  type NodesRenderer
} from 'contentful-rich-text-vue-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import type {
  Block,
  Document,
  Inline,
  Text
} from '@contentful/rich-text-types';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { FeatureTableBlock } from '@hypercodestudio/basler-components';
import type {
  FeatureTableColumn,
  FeatureTableRow,
  FeatureTableCellContent,
  FeatureTableProps
} from '@hypercodestudio/basler-components';
// Vue runtime functions like h() are auto-imported in Nuxt
import MappedComponent from '~/components/global/MappedComponent.vue';
import isChildOf from '~/utils/richtext/isChildOf';
import { createSplitParagraphRenderer } from '~/utils/richtext/createSplitParagraphRenderer';
import { isEmpty } from '~/utils/isEmpty';
import { useGlobalH2Index } from '~/composables/useGlobalH2Index';
import type { VNode } from 'vue';
import { isContentPage } from '~/utils/guards/isContentPage';
import type { IContentPage } from '~~/lib/ContentfulService';

const Link = defineAsyncComponent(
  () => import('~/components/global/contentful/Link.vue')
);

/**
 * FeatureTableCell resolves function vnodes at runtime (via resolveDynamicComponent),
 * same as titleVnode — but FeatureTableCellContent['vnode'] is typed as VNode only.
 */
function featureTableCellVnode(
  factory: () => VNode | VNode[]
): FeatureTableCellContent['vnode'] {
  return factory as unknown as FeatureTableCellContent['vnode'];
}

type RichTextNode = Block | Inline | Text;

const toArray = (value: unknown | unknown[] | null | undefined): unknown[] =>
  Array.isArray(value) ? value : value == null ? [] : [value];

const containsEmbeddedEntry = (node: RichTextNode): boolean => {
  if (
    node.nodeType === BLOCKS.EMBEDDED_ENTRY ||
    node.nodeType === INLINES.EMBEDDED_ENTRY
  ) {
    return true;
  }

  return 'content' in node && Array.isArray(node.content)
    ? (node.content as RichTextNode[]).some(containsEmbeddedEntry)
    : false;
};

interface Props {
  richText: Document;
  cols?: number;
  colsCentered?: boolean;
  contentCentered?: boolean;
  initialVisibleRows?: number;
  showMoreText?: string;
  highlightedColumns?: string;
}

const props = defineProps<Props>();

// Stable H2 ids across documents / re-renders (must match extractTocItems)
const { getIdForNode } = useGlobalH2Index();

const convertNewlinesToBreaks = (text: string) => {
  const parts = text.split('\n');
  return parts.flatMap((part, index) => {
    return index < parts.length - 1 ? [part, h('br')] : [part];
  });
};

/**
 * Checks if a <p> element only contains a single <br> (with optional whitespace).
 */
const isPOnlyWithBr = (vnode: any): boolean => {
  if (vnode?.type !== 'p' || !vnode.children) {
    return false;
  }

  const children = Array.isArray(vnode.children)
    ? vnode.children
    : [vnode.children];

  // Filter out whitespace-only text nodes
  const nonWhitespaceChildren = children.filter(
    (child: any) => !(typeof child === 'string' && child.trim() === '')
  );

  // Check if only a single <br> remains
  return (
    nonWhitespaceChildren.length === 1 &&
    nonWhitespaceChildren[0]?.type === 'br'
  );
};

/**
 * Removes leading <br> from within the first <p> tag in VNode array.
 * Also removes <p> tags that only contain a single <br>.
 * Handles cases where there might be newlines/whitespace between <p> and <br>.
 */
const stripLeadingBrFromFirstP = (vnodes: any): any => {
  if (!Array.isArray(vnodes)) {
    return vnodes;
  }

  // First, filter out <p> elements that only contain a single <br>
  const filtered = vnodes.filter((vnode) => !isPOnlyWithBr(vnode));

  return filtered.map((vnode, idx) => {
    // Only process the first <p> element
    if (idx === 0 && vnode?.type === 'p' && vnode.children) {
      const children = Array.isArray(vnode.children)
        ? vnode.children
        : [vnode.children];

      // Find the first non-whitespace child index
      let firstBrIndex = -1;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        // Skip text nodes that are only whitespace/newlines
        if (typeof child === 'string' && child.trim() === '') {
          continue;
        }
        // Check if this is a <br> element
        if (child?.type === 'br') {
          firstBrIndex = i;
        }
        break; // Stop at first non-whitespace element
      }

      // If we found a leading <br>, remove it (and preceding whitespace)
      if (firstBrIndex >= 0) {
        const newChildren = children.slice(firstBrIndex + 1);
        return { ...vnode, children: newChildren };
      }
    }
    return vnode;
  });
};

/* DBP-2495 Workaround to remove empty paragraph*/
const text = computed(() => {
  const content = props.richText;

  const lastContent = content.content[content.content.length - 1];
  if (lastContent && lastContent.content && lastContent.content.length === 1) {
    const lastNode = lastContent.content[0];

    // Type guard to check if the node is a Text node
    if (
      lastNode.nodeType === 'text' &&
      'value' in lastNode &&
      isEmpty((lastNode as Text).value)
    ) {
      delete content.content[content.content.length - 1];
    }
  }

  return content;
});
const markRenderers: MarksRenderer = {
  [MARKS.STRIKETHROUGH]: (text: Text | string[], key, _next) => {
    return h(
      'span',
      { style: 'text-decoration: line-through;', key },
      text as unknown as string
    );
  }
};

const nodesRenderer: NodesRenderer = {
  [BLOCKS.PARAGRAPH]: createSplitParagraphRenderer(),
  // [BLOCKS.TABLE]: (node, key, next) => {
  //   return h(
  //     'div',
  //     { key, class: 'table-div' },
  //     next?.(node.content as Block[], key, next)
  //   );
  // },
  // [BLOCKS.TABLE_CELL]: (node, key, next) => {
  //   return h(
  //     'div',
  //     { key, class: 'table-cell' },
  //     next?.(node.content as Block[], key, next)
  //   );
  // },
  // [BLOCKS.TABLE_ROW]: (node, key, next) => {
  //   return h(
  //     'div',
  //     { key, class: 'table-row' },
  //     next?.(node.content as Block[], key, next)
  //   );
  // },
  // [BLOCKS.TABLE_HEADER_CELL]: (node, key, next) => {
  //   return h(
  //     'div',
  //     { key, class: 'table-cell-head' },
  //     next?.(node.content as Block[], key, next)
  //   );
  // },
  [BLOCKS.EMBEDDED_ENTRY]: (node, key) =>
    h(MappedComponent, {
      key,
      item: node.data.target
    }),
  [INLINES.EMBEDDED_ENTRY]: (node, key) =>
    h(MappedComponent, {
      key,
      item: node.data.target
    }),
  [INLINES.ENTRY_HYPERLINK]: (node, key, next) => {
    if (isContentPage(node.data.target)) {
      const slug = (node.data.target as unknown as IContentPage).fields?.slug;
      if (slug) {
        return h(
          'a',
          {
            key,
            href: buildUrlString(node.data.target.sys.locale, slug),
            target: '_self'
          },
          next?.(node.content as Block[], key, next)
        );
      }
    }

    return h(
      Link,
      {
        key,
        item: node.data?.target,
        useSlot: true
      },
      {
        default: () => next?.(node.content as Block[], key, next)
      }
    );
  },
  [INLINES.ASSET_HYPERLINK]: (node, key, next) => {
    return h(
      'a',
      {
        key,
        href: node.data.target.fields?.file?.url,
        download: true,
        target: '_blank'
      },
      next?.(node.content as Block[], key, next)
    );
  },
  // H2 ids for TOC — must match extractTocItems() (getIdForNode is idempotent)
  [BLOCKS.HEADING_2]: (node, key, next) => {
    return h(
      'h2',
      {
        key,
        id: getIdForNode(node)
      },
      next?.(node.content as Block[], key, next)
    );
  },
  // [BLOCKS.TABLE_HEADER_CELL]: (node, key, next) => {
  //   return h(
  //     'th',
  //     { key, scope: 'col' },
  //     next?.(node.content as Block[], key, next)
  //   );
  // },
  [BLOCKS.OL_LIST]: (node, key, next) => {
    return h(
      'ol',
      {
        class: `level-${
          ((key?.replace('RichText--', '').split('-').length ?? 1) + 1) / 2
        }`
      },
      next?.(node.content as Block[], key, next)
    );
  },
  [BLOCKS.TABLE]: (node, key, next) => {
    const tableRows = node.content as Block[];

    if (!tableRows || tableRows.length === 0) {
      return;
    }

    // Parse highlighted columns from comma-separated string (1-based indices)
    // Handles: undefined, empty string, single number, or comma-separated list
    const highlightedSet = new Set(
      (props.highlightedColumns?.trim() || '')
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n))
    );

    // First row = header row
    const headerCells = tableRows[0].content as Block[];

    // Helper to render cell content and strip leading <br> from first <p>
    const renderCell = (content: Block[], cellKey: string) => {
      const renderedContent = content.flatMap((node, nodeIdx) => {
        if (
          node.nodeType === BLOCKS.PARAGRAPH &&
          (node.content as RichTextNode[]).some(containsEmbeddedEntry)
        ) {
          return h(
            'div',
            { key: `${cellKey}-${nodeIdx}`, class: 'rich-text-table-cell' },
            next?.(
              node.content as unknown as Block[],
              `${cellKey}-${nodeIdx}`,
              next
            )
          );
        }

        return toArray(
          next?.([node] as Block[], `${cellKey}-${nodeIdx}`, next)
        );
      });

      return stripLeadingBrFromFirstP(renderedContent);
    };

    // Top-left cell = descriptionHeader (use vnode for rich content)
    const descriptionHeader: FeatureTableCellContent | undefined =
      headerCells.length > 0
        ? {
            // Factory: FeatureTable mounts header content twice (grid + sticky clone).
            vnode: featureTableCellVnode(() =>
              renderCell(headerCells[0].content as Block[], `${key}-h-0`)
            )
          }
        : undefined;

    // Remaining header cells = columns (use titleVnode for rich content)
    // idx is 0-based, but highlightedColumns uses 1-based column numbers
    const columns: FeatureTableColumn[] = headerCells
      .slice(1)
      .map((cell, idx) => ({
        // Factory: same VNode must not be shared across main/sticky header cells.
        titleVnode: () =>
          renderCell(cell.content as Block[], `${key}-h-${idx + 1}`),
        highlighted: highlightedSet.has(idx + 1)
      }));

    // Body rows: first cell = description (row label), rest = cells
    const rows: FeatureTableRow[] = tableRows
      .slice(1)
      .map((tableRow, rowIdx) => {
        const rowCells = tableRow.content as Block[];

        // Check if first cell starts with '##' to mark as heading row
        const firstCellText =
          rowCells.length > 0
            ? documentToPlainTextString(rowCells[0]).trim()
            : '';
        const isHeadingRow = firstCellText.startsWith('##');

        // First cell is the row description
        const description: FeatureTableCellContent =
          rowCells.length > 0
            ? isHeadingRow
              ? { heading: firstCellText.replace(/^##\s*/, '') }
              : {
                  // Factory: row description is rendered in desc column and mobile-desc.
                  vnode: featureTableCellVnode(() =>
                    renderCell(
                      rowCells[0].content as Block[],
                      `${key}-r${rowIdx}-0`
                    )
                  )
                }
            : { text: '' };

        // Remaining cells
        const cells: FeatureTableCellContent[] = rowCells
          .slice(1)
          .map((cell, cellIdx) => ({
            vnode: featureTableCellVnode(() =>
              renderCell(
                cell.content as Block[],
                `${key}-r${rowIdx}-${cellIdx + 1}`
              )
            )
          }));

        return {
          description,
          cells,
          ...(isHeadingRow && { type: 'heading' as const, isGroupTitle: true })
        };
      });

    const featureTable: FeatureTableProps = {
      columns,
      rows,
      descriptionHeader,
      initialVisibleRows: props.initialVisibleRows,
      showMoreText: props.showMoreText
    };

    return h(FeatureTableBlock, {
      key,
      featureTable,
      cols: props.cols ?? 12,
      colsCentered: props.colsCentered ?? true,
      contentCentered: props.contentCentered ?? false
    });
  },
  text: (node, key, markRenderer) => {
    if (!node.marks.length) {
      // avoid hydration mismatches
      // XXX: why are "\r" included in response? not normalized in
      //  contentful? "\r" is not rendered on client side.
      const cleanedValue = node.value?.replaceAll('\r', '');

      return cleanedValue === '' &&
        !isChildOf('unordered-list', key ?? '', text.value)
        ? h('br')
        : cleanedValue.split('\n').length > 1
          ? h('span', { key }, convertNewlinesToBreaks(node.value))
          : cleanedValue;
    }

    const marksReversed = [...node.marks].reverse();

    return marksReversed.reduce(
      // XXX: proper typing
      (aggregate: any, mark, i) => {
        if (
          mark.type in markRenderer &&
          typeof markRenderer[mark.type] === 'function'
        ) {
          return markRenderer[mark.type]([aggregate], `${key}-${i}`, h);
        }
        if (node.value) {
          return aggregate;
        }
      },
      node.value
    );
  }
};
</script>
